import React, { useEffect, useState } from "react";
import Select, { type Theme } from "react-select";
import { useNavigate, useOutletContext } from "react-router";
import { toast } from "react-toastify";
import {
  ICmsSubject,
  createCmsAssessment,
  getCmsTeacherSubjects,
} from "../../../../api/crmContent.service";
import { ISelectElement } from "../../../../model/interface/keyValuePair";
import { IUserContentOutletContext } from "./UserContentLayout";
import { buildCreateAssessmentFormData } from "./assessmentHelpers";

const reactSelectTheme = (theme: Theme): Theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#B3B3B3",
    primary: "#041E42",
  },
});

export const AssessmentNew: React.FC = () => {
  const { userId } = useOutletContext<IUserContentOutletContext>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [subjects, setSubjects] = useState<Array<ISelectElement>>([]);
  const [selectedSubject, setSelectedSubject] = useState<ISelectElement | null>(
    null
  );
  const [name, setName] = useState<string>("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    getCmsTeacherSubjects(userId)
      .then((response) => {
        const options: Array<ISelectElement> = (response.data ?? []).map(
          (subject: ICmsSubject) => ({
            value: String(subject.subjectId),
            label: subject.name,
          })
        );
        setSubjects(options);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const handleDownloadTemplate = () => {
    const csvContent = "StandardId,QuestionId,IsTeacherQuestion\n";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "assessment_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const isCreateEnabled =
    selectedSubject !== null && name.trim() !== "" && csvFile !== null;

  const handleCreate = async () => {
    if (!isCreateEnabled) return;
    setIsCreating(true);
    const formData = buildCreateAssessmentFormData({
      userId,
      subjectId: selectedSubject!.value,
      name,
      csvFile: csvFile!,
    });

    try {
      const response = await createCmsAssessment(formData);
      navigate(`../assessments/${response.data}`);
    } catch (error: any) {
      toast.error(error?.response?.data || "Failed to create assessment");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="h-auto min-h-half w-full px-8 py-6">
        <div className="relative py-1">
          <div className="pt-6 border-b pb-6">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-3">
                <label className="text-sm text-gray-700">Subject</label>
                <Select
                  className="w-full mt-1 text-sm"
                  options={subjects}
                  onChange={(option) =>
                    setSelectedSubject(option as ISelectElement | null)
                  }
                  value={selectedSubject}
                  isClearable={true}
                  isSearchable={true}
                  theme={reactSelectTheme}
                  isLoading={loading}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="col-span-3">
                <label className="text-sm text-gray-700">Name</label>
                <input
                  type="text"
                  className="w-full mt-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-color"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Assessment name"
                />
              </div>

              <div className="col-span-4">
                <label className="text-sm text-gray-700">
                  Import Questions{" "}
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="text-primary-color hover:underline font-normal"
                  >
                    (download template)
                  </button>
                </label>
                <input
                  type="file"
                  accept=".csv"
                  className="w-full mt-1 text-sm"
                  onChange={(e) =>
                    setCsvFile(e.target.files ? e.target.files[0] : null)
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={handleCreate}
                disabled={!isCreateEnabled || isCreating}
                className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? "Creating..." : "Create"}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color py-1.5 px-4"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
